// ═══════════════════════════════════════════════════════════════════
//  Regresos de un servicio (bodas y parecidos)
//
//  Una boda tiene una ida y varios regresos del mismo coche: a la 1, a las
//  2, a las 3 y a las 5. El servicio es del día de la boda (ahí se asigna y
//  se cobra), pero los regresos de madrugada son, en el reloj, del día
//  siguiente, y ese día también tienen que verse.
//
//  Regla: un regreso es DE MADRUGADA si su hora es menor que la de la ida.
//  Sin hora de ida, se compara con mediodía.
//
//  La misma regla está en el servidor (server/utils/regresos.js). Si se
//  cambia aquí, hay que cambiarla allí.
// ═══════════════════════════════════════════════════════════════════

export function hhmm(v) {
  if (v === null || v === undefined || v === '') return '';
  const m = String(v).match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '';
}

export function esDeMadrugada(hora, horaSalida) {
  const h = hhmm(hora);
  if (!h) return false;
  return h < (hhmm(horaSalida) || '12:00');
}

// Regresos válidos (con hora), en el orden de la noche: primero los del mismo
// día y luego los de madrugada. Por hora a secas, las 01:00 irían antes que
// las 23:30.
export function regresosOrdenados(s) {
  // Array.isArray y no `|| []`: si `regresos` llega como texto o como objeto
  // (una respuesta a medias, un JSON mal montado), `|| []` lo deja pasar y
  // `.filter` no existe. Eso tira la pantalla ENTERA, no solo esta celda.
  const lista = Array.isArray(s?.regresos) ? s.regresos : [];
  return lista
    .filter(r => r && hhmm(r.hora))
    .slice()
    .sort((a, b) => {
      const ma = esDeMadrugada(a.hora, s.hora_salida) ? 1 : 0;
      const mb = esDeMadrugada(b.hora, s.hora_salida) ? 1 : 0;
      return ma - mb || hhmm(a.hora).localeCompare(hhmm(b.hora));
    });
}

export function regresosDeMadrugada(s) {
  return regresosOrdenados(s).filter(r => esDeMadrugada(r.hora, s.hora_salida));
}

// "2026-10-13" → "2026-10-14". En UTC para que el cambio de hora no lo líe.
//
// ESTE ERA EL DE LA PANTALLA EN BLANCO.
//
// Con una fecha que no se puede leer ('0000-00-00', '32/13/2026', un texto),
// `new Date(...)` no da error: da una fecha inválida. El que SÍ da error es
// `toISOString()`, que lanza "Invalid time value". Y esto se llama desde
// `esReflejoMadrugada`, que recorre TODOS los servicios de la agenda y del
// cuadrante: un solo servicio con la fecha mal y la pantalla entera se va.
// Ahora una fecha ilegible devuelve vacío, que es lo que ya se espera aquí
// arriba (`if (!iso) return ''`).
export function diaSiguiente(iso) {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00Z');
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * ¿Este servicio sale en `diaISO` SOLO por sus regresos de madrugada?
 * Es el caso del día 14 para una boda del 13: no es su día, pero el coche
 * sigue trabajando esa madrugada.
 */
export function esReflejoMadrugada(s, diaISO) {
  if (!s || !s.fecha || !diaISO) return false;
  const f1 = String(s.fecha).slice(0, 10);
  const f2 = s.fecha_regreso ? String(s.fecha_regreso).slice(0, 10) : f1;
  if (f1 !== f2) return false;                    // viaje de varios días: va por su cuenta
  return diaSiguiente(f1) === String(diaISO).slice(0, 10) && regresosDeMadrugada(s).length > 0;
}

// "01:00 · 02:00 · 05:00 → CARTAYA": un regreso que no va al origen lleva su sitio.
export function textoRegresos(lista) {
  return (lista || []).map(r => hhmm(r.hora) + (r.lugar ? ` → ${r.lugar}` : '')).join(' · ');
}

// "13/10": para decir de qué día viene el reflejo.
export function ddmm(iso) {
  if (!iso) return '';
  const f = String(iso).slice(0, 10);
  return `${f.slice(8, 10)}/${f.slice(5, 7)}`;
}
